import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@/components/ui/Button';
import AnnouncementAnchor from '@/components/Announcements/AnnouncementAnchor';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { NEDARIM_DRAFTS_FEATURE_KEY } from './useSupportRequestDraftsFeature';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import { useSupportRequestDrafts, useDeleteSupportRequestDraft } from './useSupportRequestDrafts';
import { mapDraftToFormData } from '@/pages/newSupportRequest/utils/mapDraftToFormData';
import styles from './DraftsButton.module.scss';

/**
 * Support requests that were filled in somewhere they could not be sent, waiting to be
 * finished here.
 *
 * A coordinator using the Nedarim Plus personal area can answer every question on the form
 * and still not send it: that app runs in an Android 4.4 WebView with no file picker, and a
 * request needs documents attached. Their answers are kept, and this is where they come back
 * for them - continuing opens the ordinary new-request form already filled in, with only the
 * documents left to add.
 *
 * It lives in the toolbar next to "new request" rather than in the page, for two reasons: the
 * two things are the same errand, and anything placed in the page displaces the list the
 * coordinator actually came to read. Renders nothing at all when there are no drafts, which is
 * the normal case for anyone who never uses that app.
 */

interface DraftsButtonProps {
  onContinue: (draft: SupportRequestDraftDtoIn) => void;
  /**
   * ⚠️ kind and featureKey travel together and must match. The two draft kinds are released
   * separately, so listing one while asking the other's flag would show a list the office has
   * not opened yet. Both omitted = the support-request draft, this component's original job.
   */
  kind?: string;
  featureKey?: string;
  /** How to name a row when the record it will become does not exist yet to be named by. */
  describe?: (draft: SupportRequestDraftDtoIn) => string;
  /**
   * Which block of heb.json the wording comes from. A second kind needs its own: Hebrew agrees
   * with the noun, so the request list's "ממתינות להשלמה" cannot be reused for supporteds.
   * Every block must carry the same five keys as `supportRequestDrafts`.
   */
  translations?: string;
}

const DraftsButton = ({
  onContinue,
  kind,
  featureKey,
  describe: describeOverride,
  translations = 'supportRequestDrafts',
}: DraftsButtonProps) => {
  const { t, i18n } = useTranslation();
  const text = (key: string, options?: Record<string, unknown>) => t(`${translations}.${key}`, options);
  const isFeatureOn = useFeature(featureKey ?? NEDARIM_DRAFTS_FEATURE_KEY);
  const { drafts } = useSupportRequestDrafts(kind, featureKey);
  const deleteDraft = useDeleteSupportRequestDraft(kind);
  const [isOpen, setIsOpen] = useState(false);
  const [discarding, setDiscarding] = useState<SupportRequestDraftDtoIn | null>(null);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  /*
   * A panel that covers page content has to be dismissable without aiming at anything, so both
   * a click outside and Escape close it. The confirmation dialog renders outside this wrapper,
   * so closing on an outside click has to stop while it is up - otherwise answering it would
   * also close the panel underneath.
   */
  useEffect(() => {
    if (!isOpen || discarding) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, discarding]);

  /*
   * Errors are not surfaced. This is an extra route into work that is also reachable by
   * starting a new request, and a coordinator who has no drafts should not be told that
   * fetching them failed.
   */
  /*
   * ⚠️ After every hook, never before: an early return above them would change the hook order
   * between renders the moment the flag or the draft list arrives, which React treats as a bug
   * in the component and not a feature being switched off.
   */
  if (!isFeatureOn || drafts.length === 0) return null;

  const describe = (draft: SupportRequestDraftDtoIn) => {
    if (describeOverride) {
      return describeOverride(draft) || text('untitled');
    }
    const values = mapDraftToFormData(draft);
    const supported = values?.supported;
    const goalName = values?.goalWithLimits?.goal?.name;

    const who = supported ? `${supported.firstName ?? ''} ${supported.lastName ?? ''}`.trim() : '';

    // A draft saved before a supported was chosen still has to say something.
    return who || (goalName ? t(goalName) : text('untitled'));
  };

  const savedAt = (draft: SupportRequestDraftDtoIn) => {
    const date = new Date(draft.updatedAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  /* Asked first, because discarding is not undoable and the answers were typed elsewhere. */
  const handleDiscard = async () => {
    if (!discarding) return;
    setIsDiscarding(true);
    try {
      await deleteDraft(discarding.id);
    } finally {
      setIsDiscarding(false);
      setDiscarding(null);
    }
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      {/* The flag's announcement (HAT-1489) points here, on whichever of the two screens comes first. */}
      <AnnouncementAnchor name={featureKey ?? NEDARIM_DRAFTS_FEATURE_KEY}>
        <Button
          variant="outline"
          className={styles.trigger}
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
        >
          {text('title')}
          <span className={`${styles.count} body-xs-regular`}>{drafts.length}</span>
        </Button>
      </AnnouncementAnchor>

      {isOpen && (
        <div className={styles.panel} role="menu" aria-label={text('title')}>
          {drafts.map((draft) => (
            <div key={draft.id} className={styles.row}>
              <button
                type="button"
                className={styles.open}
                onClick={() => {
                  setIsOpen(false);
                  onContinue(draft);
                }}
              >
                <span className={`${styles.title} body-sm-semibold`}>{describe(draft)}</span>
                <span className={`${styles.meta} body-xs-regular`}>
                  {text('savedOn', { date: savedAt(draft) })}
                  {draft.requestedAmount != null && (
                    <span className={styles.amount}>
                      {' · '}
                      {draft.requestedAmount.toLocaleString(i18n.language)} ₪
                    </span>
                  )}
                </span>
              </button>

              <button
                type="button"
                className={styles.discard}
                title={text('discard')}
                aria-label={text('discard')}
                onClick={() => setDiscarding(draft)}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmationDialog
        open={discarding !== null}
        onOpenChange={(isDialogOpen) => {
          if (!isDialogOpen && !isDiscarding) setDiscarding(null);
        }}
        title={text('discard')}
        message={text('discardConfirm')}
        confirmText={`${translations}.discard`}
        cancelText="cancel"
        variant="danger"
        isLoading={isDiscarding}
        onConfirm={handleDiscard}
      />
    </div>
  );
};

export default DraftsButton;
